import { useMemo } from 'react';

import {
  InteractiveResponseTitle,
  InteractiveResponseViewScaler,
} from '../shared/interactive-response-shared';
import { InteractiveResponseContainer } from '../interactive-response.style';
import { ViewProps } from './settings/interactive-response.constants';
import { initialise } from './play/interactive-response-sequence.utils';
import { ComponentAspectRatio } from '../../shared/component-height/component-dimensions';
import { ComponentAbsoluteWrapper } from '../../shared/component-height/component-dimensions.style';
import { useContainerDimensions } from '../../shared/component-height/component-dimensions.hooks';
import { defaultDimensions } from '../interactive-response-shared.constants';
import InteractiveDragDisplay from '../../shared/interactive-drag-drop/interactive-drag-display';
import InteractiveSequenceDropDisplay from './play/interactive-sequence-drop-display';
import type { AnswerFragment } from '../../../present/activity-answers.graphql.generated';
import { getComponentSelectorId } from '../../shared/logic/get-component-selector-id';
import type { InteractiveResponseAnswerFragment } from '../../../present/activity-answers.graphql.generated';
import { InteractiveResponsePreviewDropItemContainer } from './play/interactive-response-sequence.style';
import {
  InteractiveResponsePreviewContainer,
  InteractiveStackDragContainer,
} from '../shared/interactive-response-shared.style';
import InteractiveResponseSequencePreviewAnswer from './play/interactive-response-sequence-preview-answers';

const InteractiveResponseSequenceAnswers = (
  props: ViewProps & {
    answerAttempt?: AnswerFragment | null;
    isLoading: boolean;
    showAnswer: boolean;
  }
) => {
  const { id, properties, answerAttempt, isLoading, showAnswer } = props;
  const { interactiveResponse } = properties;

  const { dragItems, dropItems } = useMemo(() => {
    return initialise(interactiveResponse.variant, interactiveResponse.items);
  }, [interactiveResponse.variant, interactiveResponse.items]);

  const { setContainerElement } = useContainerDimensions({
    aspectRatio: 16 / 9,
  });

  return (
    <InteractiveResponseContainer
      id={getComponentSelectorId(id)}
      $isLoading={isLoading}
    >
      <ComponentAspectRatio
        dimensions={properties.responseDimensionData || defaultDimensions}
      >
        <ComponentAbsoluteWrapper ref={setContainerElement}>
          <InteractiveResponseViewScaler>
            {answerAttempt ? (
              <InteractiveResponseSequencePreviewAnswer
                {...props}
                showAnswer={showAnswer}
                attemptAnswers={
                  answerAttempt.answer as InteractiveResponseAnswerFragment
                }
              />
            ) : (
              <>
                <InteractiveResponseTitle
                  title={interactiveResponse.title}
                  subTitle={interactiveResponse.subTitle}
                />
                <InteractiveResponsePreviewContainer>
                  {dragItems.map((dragItem, index) => (
                    <InteractiveStackDragContainer
                      key={`${dragItem.id}-${index}`}
                      $index={index}
                    >
                      <InteractiveDragDisplay
                        imageUrl={dragItem.question.image?.small}
                        isFit={dragItem.question.image?.isFit}
                        description={dragItem.question.image?.description || ''}
                        baseColour={dragItem.question.baseColour}
                        variant={dragItem.question.variant}
                        text={dragItem.question.text}
                        richtext={dragItem.question.richtext}
                      />
                    </InteractiveStackDragContainer>
                  ))}
                </InteractiveResponsePreviewContainer>
                <InteractiveResponsePreviewDropItemContainer>
                  {dropItems.map(dropItem => (
                    <InteractiveSequenceDropDisplay
                      key={`${dropItem.id}`}
                      label={dropItem.label}
                      labelLength={properties.interactiveResponse.labelLength}
                      labelBorder={properties.interactiveResponse.labelBorder}
                    />
                  ))}
                </InteractiveResponsePreviewDropItemContainer>
              </>
            )}
          </InteractiveResponseViewScaler>
        </ComponentAbsoluteWrapper>
      </ComponentAspectRatio>
    </InteractiveResponseContainer>
  );
};

export default InteractiveResponseSequenceAnswers;
